<script setup lang="ts">
  defineOptions({ name: 'FormatToolbar' })

  import { computed, onMounted, onUnmounted, ref, watch, type PropType } from 'vue'

  import { Bold01Icon, Italic01Icon, Link02Icon, Strikethrough01Icon, Underline01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { AllSelection } from '@tiptap/pm/state'

  import { BubbleMenu } from '@tiptap/vue-3'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import CPButton from '@ui/components/CPButton.vue'

  import CPTooltip from '@ui/components/CPTooltip.vue'

  import { useEditor } from '../composables/useEditor'

  import type { BubbleMenuContext, FormatToolDef, FormatToolName } from '../types/editor.types'

  /**
   * Floating selection format toolbar (TipTap `BubbleMenu` + the toolbar buttons).
   *
   * The outer `<div style="display: none">` is this component's stable root. TipTap's
   * `BubbleMenu` renders a `<div>` that its plugin immediately detaches
   * (`element.remove()`) and tippy re-parents to `document.body`. If that moved
   * `<div>` were this component's root, Vue would still believe it lives in the
   * editor DOM tree, and any ancestor re-render (e.g. the host modal recomputing a
   * char count on every keystroke) would make Vue's block patch use the detached
   * node as a sibling anchor and crash with
   * "Cannot read properties of null (reading 'insertBefore')" /
   * "Cannot set properties of null (setting '__vnode')".
   *
   * Keeping the stable wrapper as the root means ancestor patches always have a
   * valid anchor; only the inner `BubbleMenu` <div> is moved to <body> by tippy,
   * and it is patched solely by this component's own (rare) re-renders.
   */
  const props = defineProps({
    tools: {
      type: Array as PropType<FormatToolName[]>,
      default: () => ['bold', 'italic', 'strikethrough', 'underline', 'link'],
    },
  })

  const emit = defineEmits<{
    addLink: []
  }>()

  const { t } = useI18n()

  const { editor: editorRef } = useEditor()

  // True once the Add/Edit Link button is pressed. Toggling it unmounts the bubble
  // menu (via the template `v-if`) so the popup disappears immediately instead of
  // lingering over the modal — the selection stays unchanged, so the default
  // selection-based `shouldShow` would otherwise keep it visible. It resets on its
  // own because the host (EditorRoot) unmounts this component on blur (the link
  // modal steals focus) and remounts it fresh — with `isLinkEmbedOpen` back to
  // `false` — on refocus.
  const isLinkEmbedOpen = ref(false)

  // Show the floating format toolbar whenever a non-empty text selection exists.
  // Skips Select-All across multiple blocks so it doesn't briefly flash on Ctrl+A.
  const shouldShow = ({ state }: BubbleMenuContext) => {
    if (isLinkEmbedOpen.value) return false
    if (state.selection instanceof AllSelection) return false
    const { from, to, empty } = state.selection
    if (empty) return false
    const docSize = state.doc.content.size
    const isWholeDoc = from <= 1 && to >= docSize - 1
    const isSingleBlockDoc = state.doc.childCount === 1
    const isSelectAll = isWholeDoc && !isSingleBlockDoc
    return !isSelectAll
  }

  // Append the toolbar tippy to <body> so it escapes any overflow / stacking
  // context on ancestor containers (post cards, modals, scroll regions).
  const appendToBody = () => document.body

  // TipTap's `isActive(...)` result depends on the current selection. `useTiptap`'s
  // ShallowRef doesn't guarantee reactivity for every transaction here, so bump a
  // manual tick on `selectionUpdate` + `transaction` and use it as a dependency
  // inside the active-state computeds below.
  const selectionTick = ref(0)

  const bumpTick = () => {
    // Only recompute active states while a real (non-empty) selection exists —
    // i.e. while the toolbar is actually shown. Skipping collapsed-cursor
    // transactions (which fire on every keystroke) avoids re-rendering the
    // tippy-detached, hidden toolbar, a patch that races with tippy's DOM moves
    // and crashes Vue with "Cannot read properties of null (reading 'insertBefore')".
    if (editorRef.value?.state?.selection?.empty === true) return
    selectionTick.value++
  }

  const isActive = (name: string, attrs?: Record<string, unknown>) => {
    // Reference selectionTick so this recomputes when the selection/transaction changes.
    void selectionTick.value
    const ed = editorRef.value
    if (!ed) return false
    return attrs ? ed.isActive(name, attrs) : ed.isActive(name)
  }

  const activeStates = computed(() => ({
    bold: isActive('bold'),
    italic: isActive('italic'),
    strikethrough: isActive('strike'),
    underline: isActive('underline'),
    link: isActive('link'),
  }))

  const runCmd = (fn: (chain: ReturnType<NonNullable<typeof editorRef.value>['chain']>) => unknown) => {
    const ed = editorRef.value
    if (!ed) return
    fn(ed.chain().focus())
  }

  const handleBold = () => runCmd(chain => chain.toggleBold().run())

  const handleItalic = () => runCmd(chain => chain.toggleItalic().run())

  const handleStrikethrough = () => runCmd(chain => chain.toggleStrike().run())

  const handleUnderline = () => runCmd(chain => chain.toggleUnderline().run())

  // If the selection already sits on a link, act as a toggle-off. Otherwise hide
  // the bubble menu immediately and defer to the parent so it can open its Add/Edit
  // Link modal.
  const handleLink = () => {
    const ed = editorRef.value
    if (ed?.isActive('link')) {
      ed.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    isLinkEmbedOpen.value = true
    emit('addLink')
  }

  const toolMap = computed<Record<FormatToolName, FormatToolDef>>(() => ({
    bold: { name: 'bold', icon: Bold01Icon, label: t('editor.format.bold'), onClick: handleBold },
    italic: { name: 'italic', icon: Italic01Icon, label: t('editor.format.italic'), onClick: handleItalic },
    strikethrough: {
      name: 'strikethrough',
      icon: Strikethrough01Icon,
      label: t('editor.format.strikethrough'),
      onClick: handleStrikethrough,
    },
    underline: { name: 'underline', icon: Underline01Icon, label: t('editor.format.underline'), onClick: handleUnderline },
    link: { name: 'link', icon: Link02Icon, label: t('editor.format.link'), onClick: handleLink },
  }))

  // Fixed group layout — hairline dividers appear between non-empty groups.
  const GROUPS: FormatToolName[][] = [['bold', 'italic'], ['strikethrough', 'underline'], ['link']]

  const visibleGroups = computed<FormatToolDef[][]>(() =>
    GROUPS.map(group => group.filter(n => props.tools.includes(n)).map(n => toolMap.value[n])).filter(group => group.length > 0)
  )

  onMounted(bumpTick)

  onUnmounted(bumpTick)

  watch(
    editorRef,
    (ed, _prev, onCleanup) => {
      if (!ed) return
      ed.on('selectionUpdate', bumpTick)
      ed.on('transaction', bumpTick)
      onCleanup(() => {
        ed.off('selectionUpdate', bumpTick)
        ed.off('transaction', bumpTick)
      })
    },
    { immediate: true }
  )
</script>

<template>
  <!--
    Stable, zero-footprint anchor. Tippy moves the inner BubbleMenu <div> out to
    <body>, but this wrapper <div> stays put so ancestor re-renders never patch
    across a detached node. `display: none` keeps it out of layout; the actual
    toolbar is rendered by tippy in <body>, unaffected by this style.
  -->
  <div aria-hidden="true" style="display: none">
    <BubbleMenu
      v-if="editorRef && !isLinkEmbedOpen"
      :editor="editorRef"
      :tippy-options="{
        duration: 100,
        placement: 'top',
        animation: 'fade',
        maxWidth: 'none',
        arrow: false,
        theme: 'cp-format-toolbar',
        zIndex: 999999,
        appendTo: appendToBody,
      }"
      :should-show="shouldShow"
    >
      <div :id="createElementId('editor', 'format', 'toolbar')" class="format-toolbar">
        <template v-for="(group, gi) in visibleGroups" :key="gi">
          <span v-if="gi > 0" :id="createElementId('editor', 'format', `divider-${gi}`)" class="format-divider" aria-hidden="true" />
          <div class="format-group">
            <CPTooltip v-for="tool in group" :key="tool.name" placement="top" :z-index="1000000">
              <template #trigger>
                <!-- `mousedown.prevent` keeps focus in the editor so the container's
                     focus-ring doesn't animate out and back in on every click. Click
                     still fires normally; only the default focus change is blocked. -->
                <CPButton
                  :id="createElementId('editor', 'format', tool.name)"
                  type="ghost"
                  color="transparent"
                  class="format-btn"
                  :class="{ 'format-btn--active': activeStates[tool.name] }"
                  :aria-label="tool.label"
                  :aria-pressed="activeStates[tool.name]"
                  @mousedown.prevent
                  @click="tool.onClick"
                >
                  <component :is="tool.icon" class="format-btn__icon" />
                </CPButton>
              </template>
              <div class="max-w-[14rem] px-2 py-1 text-xs">{{ tool.label }}</div>
            </CPTooltip>
          </div>
        </template>
      </div>
    </BubbleMenu>
  </div>
</template>

<style scoped>
  .format-toolbar {
    display: inline-flex;
    align-items: center;
    gap: 0;
    padding: 0.25rem;
    background-color: var(--cp-brand-background);
    border: 1px solid var(--cp-border-500);
    border-radius: 0.75rem;
    box-shadow:
      0 1px 2px rgba(16, 24, 40, 0.04),
      0 8px 16px -4px rgba(16, 24, 40, 0.08);
    /* Keeps the toolbar contained within its parent (the editor container in the modal). */
    max-width: 100%;
  }

  .format-group {
    display: inline-flex;
    align-items: center;
    gap: 0.125rem;
  }

  .format-divider {
    display: inline-block;
    width: 1px;
    height: 1.25rem;
    margin: 0 0.375rem;
    background: var(--cp-border-500);
    flex-shrink: 0;
  }

  .format-btn,
  .format-btn:hover,
  .format-btn:focus,
  .format-btn:active {
    background: transparent !important;
    padding: 0 !important;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    color: var(--cp-font-700);
    min-width: 0;
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    transition:
      background-color 0.12s ease,
      color 0.12s ease;
  }

  .format-btn:hover {
    background: var(--cp-brand-border) !important;
  }

  .format-btn--active,
  .format-btn--active:hover,
  .format-btn--active:focus {
    background: var(--cp-primary-100) !important;
    color: var(--cp-primary-700) !important;
  }

  .format-btn__icon {
    width: 1rem;
    height: 1rem;
  }
</style>
